/**
 * customer-bootstrap · 类型与常量
 *
 * 本文件零运行副作用：
 *   - 只导出 const / type / interface
 *   - 编译后只剩 const 字面量
 *
 * 来源映射：
 *   js/bootstrap-preflight.js  预设（APP_VERSION / 主题 / 背景）
 *   customer-bootstrap.bundle.js  密码闸 + 占用上报 + 强制登出 WS
 */

export const APP_VERSION = '3.1.0';
export const DEFAULT_PWD = '123456';
export const PWD_LENGTH = 6;

export const STORAGE_KEYS = {
  systemSettings: 'restaurantSystemSettings',
  networkConfig: 'restaurantNetworkConfig',
  customerSession: 'restaurantCustomerSession',
  customerTheme: 'restaurantCustomerTheme',
} as const;

/** 刷新恢复后补发占用通知的两次延时（ms） */
export const RESTORE_NOTIFY_DELAYS_MS = [200, 800] as const;

/** 强制登出 WS：断线重连间隔 */
export const FORCE_LOGOUT_RECONNECT_MS = 2500;

/** 强制登出后给 bundle 留出清理时间，再整页 reload */
export const RELOAD_DELAY_MS = 120;

/** WS 建连首次延时（等 bundle 把 networkConfig 写好） */
export const FORCE_LOGOUT_INITIAL_DELAY_MS = 400;

/** 背景预设白名单（与 preflight 一致） */
export type BgPresetName =
  | 'white'
  | 'black'
  | 'gray'
  | 'sky_blue'
  | 'lake_green'
  | 'light_purple'
  | 'china_red';

export const BG_PRESET_ALLOWED: Record<BgPresetName, true> = {
  white: true,
  black: true,
  gray: true,
  sky_blue: true,
  lake_green: true,
  light_purple: true,
  china_red: true,
};

/** 历史名 → 现名映射（与 preflight 一致） */
export const BG_PRESET_LEGACY: Record<string, BgPresetName> = {
  light_green: 'lake_green',
  light_blue: 'sky_blue',
  light_gray: 'gray',
  light_red: 'china_red',
};

/** 背景预设默认值（与 preflight 一致） */
export const BG_PRESET_DEFAULT: BgPresetName = 'black';

/** osPreview ?w= 合法宽度区间 + 默认 */
export const OS_PREVIEW_WIDTH_MIN = 280;
export const OS_PREVIEW_WIDTH_MAX = 1200;
export const OS_PREVIEW_WIDTH_DEFAULT = 393;

/**
 * 主题偏好：与原 inline 行为对齐——
 *   - 'dark' / 'light' 来自 localStorage.restaurantCustomerTheme
 *   - 没存值时回落 matchMedia('prefers-color-scheme: dark')，记为 'system'
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** localStorage.restaurantSystemSettings 关心的字段（其余忽略） */
export interface SystemSettings {
  customerLiteLoginPassword?: string;
  customerBackgroundPreset?: string;
}

/** localStorage.restaurantNetworkConfig */
export interface NetworkConfig {
  apiBaseUrl?: string;
}

/** sessionStorage.restaurantCustomerSession（bootstrap 只关心 table/guests） */
export interface CustomerSession {
  table: string | number;
  guests?: number;
  slots?: string[];
  [key: string]: unknown;
}

/** WS 推送：admin 端清空台桌 */
export interface ForceLogoutMessage {
  type: 'table-history-cleared';
  table: string | number;
}

/** index.html <head> 预设脚本输出（FOUC 防闪）。便于以后做单测 */
export interface PreflightResult {
  theme: ThemePreference;
  /** 经过白名单 / legacy 映射归一化后的有效值；总是非空（默认 'black'） */
  bgPreset: BgPresetName;
  isOsPreview: boolean;
  isEmbedded: boolean;
  dark: boolean;
  /** osPreview 模式下应用的 viewport 宽度；非 osPreview 时为 0 */
  osPreviewWidth: number;
}

/** 与 window.Ordersystem 的最小握手契约（仅 bootstrap 用到这几项） */
export interface OrdersystemFacade {
  getNetworkConfig?: () => NetworkConfig | null | undefined;
  clearCustomerSession?: () => void;
}

declare global {
  interface Window {
    APP_VERSION?: string;
    Ordersystem?: OrdersystemFacade;
    OS?: { facade?: OrdersystemFacade } & Record<string, unknown>;
  }
}
